import { useId, useState } from "react";
import { AlertCircle, AlertTriangle, Building2, CheckCircle2, Clock, Download, FileText, History, Loader2, MessageSquare, UserCheck, X, XCircle } from "lucide-react";
import { cn } from "@/lib/cn";
import { useDrawerA11y } from "@/hooks/useDrawerA11y";
import { useRequestDetail, usePatchRequest, useClaimRequest, useCommentRequest, useAnswerRequest } from "@/hooks/useInternalRequests";
import { CompleteRequestModal } from "@/components/requests/CompleteRequestModal";
import {
  REQUEST_STATUS_LABEL, REQUEST_TYPE_LABEL, requestAttachmentDownloadUrl,
  type RequestStatus,
} from "@/api/internalRequests";

/**
 * #9 S2 — Request Detail Drawer (TZ §12.4): запит + thread + history + result +
 * attachments. Participant-gated (backend NULL=DENY). Requester-side дії в S2 =
 * «Скасувати запит» (canCancel); claim/assign/complete-modal — S3, comment/answer
 * input — S4. Кнопки строго allowedActions-gated (no dead CTA §0/§19).
 */

const STATUS_CLS: Record<RequestStatus, string> = {
  new: "bg-info text-info-foreground",
  assigned: "bg-info text-info-foreground",
  in_progress: "bg-warning text-warning-foreground",
  // DS-CANON-ALLOW: needs_info attention orange (D-096 DR-1=C) — ЄДИНИЙ дозволений raw-orange
  // (attention/awaiting-answer стан, не error). Задокументований виняток AC-DS-CANON; не множити.
  needs_info: "bg-orange-50 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300",
  done: "bg-success text-success-foreground",
  rejected: "bg-destructive-soft text-destructive-soft-foreground", // DS-2.1: AA 5.47:1 (D-096 DR-2)
  cancelled: "bg-muted text-muted-foreground",
};

const FORM_LABEL: Record<string, string> = {
  periodFrom: "Період з", periodTo: "Період по",
  materialType: "Тип матеріалу", brand: "Бренд", city: "Місто",
  campaignNote: "Суть акції", changeDescription: "Суть змін",
  field: "Поле / реквізит", currentValue: "Поточне значення", newValue: "Нове значення",
};

const RESULT_STATUS_LABEL: Record<string, string> = {
  completed: "виконано",
  completed_with_changes: "виконано зі змінами",
  redirected: "перенаправлено",
  duplicate: "дублікат",
};

const KIND_LABEL: Record<string, string> = {
  comment: "коментар", needs_info: "питання відділу", answer: "відповідь автора", result: "результат",
};

function fmtDt(s: string | null | undefined): string {
  if (!s) return "—";
  return s.replace("T", " ").slice(0, 16);
}

export function RequestDetailDrawer({ requestId, onClose }: { requestId: string | null; onClose: () => void }) {
  const open = !!requestId;
  const headingId = useId();
  const panelRef = useDrawerA11y(open, onClose);
  const q = useRequestDetail(requestId);
  const patch = usePatchRequest();
  const claim = useClaimRequest();
  const commentMut = useCommentRequest();
  const answerMut = useAnswerRequest();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [completeMode, setCompleteMode] = useState<"complete" | "reject" | null>(null);
  const [commentText, setCommentText] = useState("");
  const [answerText, setAnswerText] = useState("");
  const [askOpen, setAskOpen] = useState(false);
  const [askText, setAskText] = useState("");
  const [err, setErr] = useState<string | null>(null);

  if (!open) return null;

  const d = q.data;
  const r = d?.request;
  const lbl = "text-[11px] font-semibold uppercase tracking-wide text-muted-foreground";

  const doCancel = async () => {
    if (!r) return;
    try {
      await patch.mutateAsync({ id: r.id, status: "cancelled" });
      setConfirmCancel(false);
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Не вдалося скасувати");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose} aria-hidden>
      <div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={headingId} tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="relative flex h-full w-full flex-col overflow-y-auto bg-background shadow-2xl outline-none sm:w-[520px]">
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b bg-background px-5 py-4">
          <div className="min-w-0">
            <h3 id={headingId} className="truncate text-base font-semibold">
              {r ? (
                <>
                  {r.requestNumber && <span className="mr-1.5 font-mono text-[13px] text-muted-foreground">{r.requestNumber}</span>}
                  {r.subject}
                </>
              ) : "Запит"}
            </h3>
            {r && (
              <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                <span className={cn("inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold", STATUS_CLS[r.status])}>
                  {REQUEST_STATUS_LABEL[r.status]}
                </span>
                {(d?.request.completeDueAt && ["new", "assigned", "in_progress"].includes(r.status)
                  && new Date(d.request.completeDueAt.replace(" ", "T")) < new Date()) && (
                  <span className="inline-flex items-center gap-0.5 font-medium text-warning-foreground">
                    <AlertTriangle className="h-3 w-3" /> SLA прострочено
                  </span>
                )}
                <span>{REQUEST_TYPE_LABEL[r.requestType as keyof typeof REQUEST_TYPE_LABEL] ?? r.requestType}</span>
                {r.departmentName && <span>· {r.departmentName}</span>}
              </div>
            )}
          </div>
          <button type="button" onClick={onClose} aria-label="Закрити" className="rounded-md p-1 text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 space-y-5 px-5 py-5">
          {q.isPending && <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-14 animate-pulse rounded-lg bg-muted" />)}</div>}
          {q.isError && (
            <div className="flex items-center gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4" /> {q.error instanceof Error ? q.error.message : "Немає доступу до запиту"}
            </div>
          )}

          {r && (
            <>
              {/* meta */}
              <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 rounded-xl border bg-muted/20 px-4 py-3 text-sm">
                <div><div className={lbl}>Автор</div>{r.requesterName ?? "—"}</div>
                <div><div className={lbl}>Виконавець</div>{r.assigneeName ?? <span className="text-muted-foreground">у черзі відділу</span>}</div>
                <div><div className={lbl}>Створено</div>{fmtDt(r.createdAt)}</div>
                <div><div className={lbl}>Дедлайн</div>{r.dueDate ?? "—"}{r.desiredDueDate && <span className="text-muted-foreground"> · бажано {r.desiredDueDate}</span>}</div>
                {(r.claimDueAt || r.completeDueAt) && (
                  <div className="col-span-2 flex items-center gap-1.5 text-[12px] text-muted-foreground">
                    <Clock className="h-3.5 w-3.5" />
                    SLA: взяти до {fmtDt(r.claimDueAt)} · виконати до {fmtDt(r.completeDueAt)} (календарно)
                  </div>
                )}
              </div>

              {/* client context */}
              {d.context.holdingName && (
                <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                  <Building2 className="h-4 w-4" /> {d.context.holdingName}
                </div>
              )}

              {/* type-specific formData */}
              {r.formData && Object.keys(r.formData).length > 0 && (
                <div className="rounded-xl border px-4 py-3">
                  <div className={cn(lbl, "mb-2")}>Дані запиту</div>
                  <dl className="space-y-1 text-sm">
                    {Object.entries(r.formData).map(([k, v]) => (
                      <div key={k} className="flex gap-2">
                        <dt className="w-40 shrink-0 text-muted-foreground">{FORM_LABEL[k] ?? k}</dt>
                        <dd className="min-w-0 break-words">{v}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}

              {/* body */}
              {r.body && <p className="whitespace-pre-wrap text-sm">{r.body}</p>}

              {/* result / reject */}
              {(r.result || r.rejectReason) && (
                <div className={cn("rounded-xl border px-4 py-3",
                  r.status === "rejected" ? "border-destructive/30 bg-destructive-soft"
                    : "border-success-foreground/20 bg-success")}>
                  <div className={cn(lbl, "mb-1")}>
                    {r.status === "rejected" ? "Причина відмови" : "Результат"}
                    {r.resultStatus && <span className="ml-1.5 normal-case">· {RESULT_STATUS_LABEL[r.resultStatus] ?? r.resultStatus}</span>}
                  </div>
                  <p className="whitespace-pre-wrap text-sm">{r.rejectReason ?? r.result}</p>
                </div>
              )}

              {/* attachments */}
              {d.attachments.length > 0 && (
                <div>
                  <div className={cn(lbl, "mb-1.5 flex items-center gap-1")}><FileText className="h-3.5 w-3.5" /> Вкладення</div>
                  <ul className="space-y-1">
                    {d.attachments.map((a) => (
                      <li key={a.id}>
                        <a href={requestAttachmentDownloadUrl(r.id, a.id)} download
                          className="flex items-center justify-between rounded-md border bg-muted/30 px-2.5 py-1.5 text-[12.5px] hover:bg-muted/60">
                          <span className="truncate">{a.fileName} <span className="text-muted-foreground">({Math.round(a.fileSize / 1024)} KB)</span></span>
                          <Download className="ml-2 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* S4 — answer-банер (requester, needs_info) */}
              {d.allowedActions.canAnswer && (
                <div className="rounded-xl border border-orange-300 bg-orange-50/60 px-4 py-3 dark:border-orange-900/50 dark:bg-orange-950/20">
                  <div className={cn(lbl, "mb-1.5 text-orange-700 dark:text-orange-300")}>Відділ чекає вашої відповіді (SLA на паузі)</div>
                  <textarea rows={2} value={answerText} onChange={(e) => setAnswerText(e.target.value)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="Ваша відповідь…" />
                  <button type="button" disabled={answerText.trim() === "" || answerMut.isPending}
                    onClick={async () => {
                      try { await answerMut.mutateAsync({ id: r.id, body: answerText.trim() }); setAnswerText(""); }
                      catch (e) { setErr(e instanceof Error ? e.message : "Не вдалося відповісти"); }
                    }}
                    className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-orange-600 px-3 py-2 text-sm font-semibold text-white hover:bg-orange-700 disabled:opacity-50">
                    {answerMut.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Відповісти
                  </button>
                </div>
              )}

              {/* thread */}
              <div>
                <div className={cn(lbl, "mb-1.5 flex items-center gap-1")}><MessageSquare className="h-3.5 w-3.5" /> Обговорення</div>
                {d.comments.length === 0 ? (
                  <p className="rounded-md border border-dashed px-3 py-3 text-center text-xs text-muted-foreground">Коментарів ще немає.</p>
                ) : (
                  <ul className="space-y-2">
                    {d.comments.map((c) => (
                      <li key={c.id} className={cn("rounded-lg border px-3 py-2 text-sm",
                        // DS-CANON-ALLOW: needs_info attention orange (D-096 DR-1=C) — див. STATUS_CLS
                        c.kind === "needs_info" && "border-orange-200 bg-orange-50/50 dark:border-orange-900/40 dark:bg-orange-950/20",
                        c.kind === "answer" && "border-info-foreground/20 bg-info",
                        c.kind === "result" && "border-success-foreground/20 bg-success")}>
                        <div className="mb-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                          <b className="text-foreground">{c.authorName ?? "—"}</b>
                          · {KIND_LABEL[c.kind] ?? c.kind} · {fmtDt(c.createdAt)}
                        </div>
                        <p className="whitespace-pre-wrap">{c.body}</p>
                      </li>
                    ))}
                  </ul>
                )}
                {/* S4 — comment input (participants, open request) */}
                {d.allowedActions.canComment && (
                  <div className="mt-2 flex items-start gap-2">
                    <textarea rows={1} value={commentText} onChange={(e) => setCommentText(e.target.value)}
                      className="min-h-[38px] flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="Коментар…" />
                    <button type="button" disabled={commentText.trim() === "" || commentMut.isPending}
                      onClick={async () => {
                        try { await commentMut.mutateAsync({ id: r.id, body: commentText.trim() }); setCommentText(""); }
                        catch (e) { setErr(e instanceof Error ? e.message : "Не вдалося додати коментар"); }
                      }}
                      className="rounded-md border px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground disabled:opacity-50">
                      {commentMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Надіслати"}
                    </button>
                  </div>
                )}
              </div>

              {/* history */}
              {d.history.length > 0 && (
                <div>
                  <div className={cn(lbl, "mb-1.5 flex items-center gap-1")}><History className="h-3.5 w-3.5" /> Історія статусів</div>
                  <ul className="space-y-0.5 text-[12px] text-muted-foreground">
                    {d.history.map((hh, i) => (
                      <li key={i}>
                        {fmtDt(hh.at)} · {hh.fromStatus ? `${REQUEST_STATUS_LABEL[hh.fromStatus as RequestStatus] ?? hh.fromStatus} → ` : ""}
                        <b className="text-foreground">{REQUEST_STATUS_LABEL[hh.toStatus as RequestStatus] ?? hh.toStatus}</b>
                        {hh.actorName && ` · ${hh.actorName}`}{hh.note && ` — ${hh.note}`}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {err && <p role="alert" className="flex items-center gap-1.5 text-xs text-destructive"><AlertCircle className="h-3.5 w-3.5" /> {err}</p>}
            </>
          )}
        </div>

        {/* allowedActions-gated footer: dept-side (claim/start/complete/reject, S3)
            + requester-side cancel (S2). No dead CTA — кнопка є лише коли можна. */}
        {r && (d.allowedActions.canClaim || d.allowedActions.canStart || d.allowedActions.canComplete
          || d.allowedActions.canReject || d.allowedActions.canCancel) && (
          <div className="sticky bottom-0 border-t bg-background px-5 py-3.5">
            {confirmCancel ? (
              <div className="flex items-center gap-2">
                <span className="mr-auto text-sm text-muted-foreground">Скасувати запит?</span>
                <button type="button" onClick={() => void doCancel()} disabled={patch.isPending}
                  className="inline-flex items-center gap-1.5 rounded-md bg-destructive px-3 py-2 text-sm font-medium text-destructive-foreground disabled:opacity-50">
                  {patch.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Так, скасувати
                </button>
                <button type="button" onClick={() => setConfirmCancel(false)} className="rounded-md border px-3 py-2 text-sm text-muted-foreground hover:text-foreground">Ні</button>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                {d.allowedActions.canClaim && (
                  <button type="button" disabled={claim.isPending} onClick={() => claim.mutate(r.id)}
                    className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
                    {claim.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserCheck className="h-3.5 w-3.5" />} Взяти в роботу
                  </button>
                )}
                {d.allowedActions.canStart && (
                  <button type="button" disabled={patch.isPending} onClick={() => patch.mutate({ id: r.id, status: "in_progress" })}
                    className="rounded-md border px-3 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50">
                    Почати
                  </button>
                )}
                {d.allowedActions.canComplete && (
                  <button type="button" onClick={() => setCompleteMode("complete")}
                    className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary-dark">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Виконано
                  </button>
                )}
                {d.allowedActions.canRequestInfo && (
                  <button type="button" onClick={() => setAskOpen((v) => !v)}
                    className="rounded-md border border-orange-300 px-3 py-2 text-sm font-medium text-orange-700 hover:bg-orange-50 dark:border-orange-900/50 dark:text-orange-300 dark:hover:bg-orange-950/20">
                    Потрібне уточнення
                  </button>
                )}
                {d.allowedActions.canReject && (
                  <button type="button" onClick={() => setCompleteMode("reject")}
                    className="inline-flex items-center gap-1.5 rounded-md border border-destructive/40 px-3 py-2 text-sm font-medium text-destructive hover:bg-destructive/5">
                    <XCircle className="h-3.5 w-3.5" /> Відхилити
                  </button>
                )}
                {d.allowedActions.canCancel && (
                  <button type="button" onClick={() => setConfirmCancel(true)}
                    className="ml-auto rounded-md border border-destructive/40 px-3 py-2 text-sm font-medium text-destructive hover:bg-destructive/5">
                    Скасувати запит
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* S4 — needs_info question box (assignee/head) */}
        {r && askOpen && d?.allowedActions.canRequestInfo && (
          <div className="border-t bg-orange-50/40 px-5 py-3 dark:bg-orange-950/10">
            <div className="flex items-start gap-2">
              <textarea rows={2} value={askText} onChange={(e) => setAskText(e.target.value)} autoFocus
                className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm" placeholder="Питання до автора запиту…" />
              <button type="button" disabled={askText.trim() === "" || patch.isPending}
                onClick={async () => {
                  try {
                    await patch.mutateAsync({ id: r.id, status: "needs_info", question: askText.trim() });
                    setAskText(""); setAskOpen(false);
                  } catch (e) { setErr(e instanceof Error ? e.message : "Не вдалося надіслати питання"); }
                }}
                className="rounded-md bg-orange-600 px-3 py-2 text-sm font-semibold text-white hover:bg-orange-700 disabled:opacity-50">
                {patch.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Запитати"}
              </button>
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">SLA стане на паузу до відповіді автора.</p>
          </div>
        )}

        {r && (
          <CompleteRequestModal
            target={completeMode ? { id: r.id, subject: r.subject, requestNumber: r.requestNumber } : null}
            mode={completeMode ?? "complete"}
            onClose={() => setCompleteMode(null)}
          />
        )}
      </div>
    </div>
  );
}
